import { Box, Button, Flex, Heading, Spinner, Stack, Text, useToast } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Attribute, BasicAttributeValue, RoleAccount } from 'src/api/v1-api';
import { useAuth } from 'src/utils/auth';
import useRequest from 'src/utils/hooks/useRequest';
import NextLink from 'next/link';
import { apiRequest } from 'src/utils/fetchUtils';
import { PaymentAlert } from 'src/components/atoms/payment-alert';
import AttributeList from 'src/components/molecules/attribute-list';
import { checkAttributeValidity, flattenAttributeValues } from 'src/utils/attributeUtils';
import { getRoleAccountUrl } from 'src/utils/marketplaceUtils';
import { MarketplaceContext } from 'src/contexts/marketplace';
import { useDeepEffect } from 'src/utils/hooks';
import MarketplaceRoleBadge from 'src/components/atoms/marketplacerole-status-badge';
import RolePriceCard from 'src/components/molecules/role-price-card';
import CancelSubscriptionButton from 'src/components/atoms/cancel-subscription-button';

const MarketplaceRoleAccountForm = (props: { roleAccount?: RoleAccount }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [role, setRole] = useState<RoleAccount | null>(props.roleAccount ?? null);
  const preauthOnly = role?.marketplace_role?.functional?.preauth_only ?? false;
  const [attributeValues, setAttributeValues] = useState<BasicAttributeValue[]>(
    props.roleAccount?.role_attribute_values ?? [],
  );
  const [attributes, setAttributes] = useState<Attribute[]>(
    props.roleAccount?.marketplace_role?.attributes ?? [],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const router = useRouter();
  const { id } = router.query;
  const roleId = props.roleAccount?.id ?? Number(id as string);
  const { api } = useRequest();
  const { user, getToken, updateUser } = useAuth();
  const { marketplace, urlBase } = useContext(MarketplaceContext);
  const toast = useToast();

  const fetchRole = useCallback(
    async (roleId: number) => {
      if (!user || !roleId) return;
      setIsLoading(true);
      try {
        setIsLoading(true);
        const response = await api.users.account.roles.get(Number(user?.id), roleId);
        setRole(response);
        setAttributes(response.marketplace_role.attributes);
        setAttributeValues(response.role_attribute_values);
      } catch (error) {
        toast({
          title: 'Error',
          description:
            'Something went wrong, please try again. Contact support if the issue persists.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [setRole, setIsLoading, roleId, user],
  );

  useEffect(() => {
    if (!roleId || props.roleAccount) return;
    fetchRole(roleId);
  }, [roleId, user]);

  useDeepEffect(() => {
    const fetchCheckoutSession = async (marketplaceId: number, checkoutSessionId: string) => {
      const checkoutSession = await api.checkoutSession.get(marketplaceId, checkoutSessionId);
      if (checkoutSession.status === 'complete') {
        toast({
          title: 'Success',
          description:
            'Your payment was successfully processed! It may take up to a minute for the payment to process, try refreshing if the changes are not reflected immediately',
          status: 'success',
          duration: 9000,
          isClosable: true,
        });
      }
    };

    if (router.isReady && router.query.checkout_session_id && marketplace) {
      const checkoutSessionId = router.query.checkout_session_id as string;
      fetchCheckoutSession(marketplace.id, checkoutSessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query, marketplace]);

  const checkFormValidity = async () => {
    let isValid = true;
    let warningMessage = '';

    const error_message = checkAttributeValidity(
      attributes,
      flattenAttributeValues(attributeValues),
    );
    if (error_message !== '') {
      warningMessage = error_message;
      isValid = false;
    }

    if (!isValid) {
      toast({
        title: warningMessage,
        status: 'warning',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    }
    return isValid;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const validForm = await checkFormValidity();
    if (!validForm) {
      setIsSubmitting(false);
      return;
    }
    const token = await getToken();
    const url = `${process.env.NEXT_PUBLIC_API_HOST}/users/${user?.id}/roles/${roleId}/update/`;

    const formData = new FormData();

    formData.append('role_attribute_values', JSON.stringify(attributeValues));

    try {
      await apiRequest('PATCH', url, token, formData, true);
      toast({
        title: 'Success',
        description: 'Account information saved.',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'bottom',
      });
      // Re-init the user object
      updateUser();
    } catch (error) {
      toast({
        title: 'Error.',
        description: 'Unable to save account information. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    }
    setIsSubmitting(false);
  };

  const fetchClientSecret = useCallback(() => {
    if (!roleId || !user?.id || urlBase === '') return Promise.resolve('');
    const roleUrl = getRoleAccountUrl(urlBase, roleId);
    const payload = {
      return_url: roleUrl,
      client_reference_id: `${roleId.toString()}-role_account_id`,
      preauth: preauthOnly,
    };
    return api.users.account.roles.createCheckoutSession(Number(user.id), roleId, payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId, user, urlBase]);

  const hasActiveSubscription = useMemo(() => {
    return (
      role?.marketplace_role?.functional?.requires_payment &&
      role?.marketplace_role?.functional?.monthly_subscription &&
      role?.status === 'active'
    );
  }, [role]);

  const onCancelSubscription = async () => {
    if (!role?.id || !user?.id) return;

    try {
      await api.marketplace.user.roles.cancelSubscription(user.id, role.id);
      toast({
        title: 'Subscription Cancelled',
        description: 'It will remain active until the end of the billing cycle.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      await fetchRole(role.id);
    } catch {
      toast({
        title: 'Error',
        description: 'There was an error cancelling the subscription. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      {isLoading ? (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          justifyContent="center"
          alignItems="center"
          backgroundColor="rgba(255, 255, 255, 0.7)"
        >
          <Spinner />
        </Box>
      ) : (
        <form onSubmit={handleSubmit}>
          {role && role.status === 'pending_payment' && !preauthOnly && (
            <PaymentAlert
              fetchClientSecret={fetchClientSecret}
              message="Complete your payment to use this Role in your account."
              btnMessage="Pay to Activate"
              alertMessage="Note: It may take up to a minute for the payment to process, try refreshing if the changes are not reflected immediately."
            />
          )}
          {role && role.status === 'pending_payment' && preauthOnly && (
            <PaymentAlert
              fetchClientSecret={fetchClientSecret}
              message="Pre-authorize your credit card to bid on auctions."
              btnMessage="Authorize"
              alertMessage="This fee will only be charged if you are top bidder at the completion of the auction and do not complete the sale in good faith. Your card is not being charged at this time."
            />
          )}

          <Stack>
            <Text as="b" fontSize="xl" w="full" textAlign="start">
              {role?.marketplace_role?.name ?? ''} &nbsp;
              <MarketplaceRoleBadge status={role?.status ?? ''} preAuth={preauthOnly} />
            </Text>

            {role?.marketplace_role?.functional?.requires_payment && <RolePriceCard role={role} />}

            {role && role.status === 'active' && (
              <Flex align="center" gap={4} direction={'row'} wrap={'wrap'}>
                <Button
                  colorScheme="primary"
                  onClick={() => {
                    const previousUrl = document.referrer || '/';
                    // Force a hard navigation to the previous page to ensure the user object is loaded in correctly
                    window.location.href = previousUrl;
                  }}
                >
                  Back to Previous Page
                </Button>
                {hasActiveSubscription && (
                  <CancelSubscriptionButton
                    cancellationRequested={role?.sub_status === 'cancellation_requested'}
                    onCancelSubscription={onCancelSubscription}
                  />
                )}
              </Flex>
            )}

            {attributes.length !== 0 && (
              <>
                <Heading lineHeight="1.5" fontWeight="semibold" fontSize="16px">
                  Attributes
                </Heading>

                <Box position="relative">
                  <Stack justify="flex-start" align="flex-start" spacing="20px">
                    {/* Loop through attributes and render them here */}
                    <AttributeList
                      attributes={attributes}
                      attributeValues={attributeValues}
                      setAttributeValues={setAttributeValues}
                      direction="column"
                    />
                  </Stack>
                </Box>
              </>
            )}
          </Stack>
          {(role?.marketplace_role?.attributes ?? []).length !== 0 && (
            <Box display="flex" justifyContent="flex-end" pt={10}>
              <NextLink href="/">
                <Button variant="outline" colorScheme="primary" mr={4}>
                  Cancel
                </Button>
              </NextLink>
              <Button isLoading={isSubmitting} colorScheme="primary" type="submit">
                Save
              </Button>
            </Box>
          )}
        </form>
      )}
    </>
  );
};

export default MarketplaceRoleAccountForm;
